const gaId = () => import.meta.env.VITE_GA_MEASUREMENT_ID;
const ymId = () => Number(import.meta.env.VITE_YANDEX_METRIKA_ID) || undefined;

type YmFn = ((...args: unknown[]) => void) & { a?: unknown[][]; l?: number };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    ym?: YmFn;
  }
}

function loadScript(src: string) {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/**
 * Loads Google Analytics 4 and Yandex Metrika. Automatic page views are off:
 * the SPA reports them itself via {@link trackPageView} on every route change,
 * the first one included. Each counter is skipped when its ID env var is unset;
 * no-op outside production builds.
 */
export function initAnalytics() {
  if (!import.meta.env.PROD) return;

  const GA_ID = gaId();
  const YM_ID = ymId();

  if (GA_ID) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, { send_page_view: false });
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
  }

  if (YM_ID) {
  const queue: unknown[][] = [];
  const ym = Object.assign(
    (...args: unknown[]) => {
      queue.push(args);
    },
    { a: queue, l: Date.now() },
  );
  window.ym = ym;
  window.ym(YM_ID, 'init', {
    defer: true,
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
  });
  loadScript('https://mc.yandex.ru/metrika/tag.js');
  }
}

/** Reports a virtual page view to both counters. Pass the path only, never tokens in the query string. */
export function trackPageView(path: string) {
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
  const YM_ID = ymId();
  if (YM_ID) window.ym?.(YM_ID, 'hit', window.location.origin + path, { title: document.title });
}
